import { Data, Route } from '@angular/router';

/**
 * Menú lateral derivado del router.
 *
 * Cada ruta que es entrada de menú declara `data.menu` (RouteMenuData). El
 * layout recorre `Router.config` y muestra una entrada sólo si el mismo
 * predicado del guard de acceso pasa para esa ruta (sumando los requisitos de
 * sus padres). Así el guard, el menú y la pantalla "Sin acceso" no pueden
 * contradecirse: si una entrada aparece, la ruta abre; si no abre, no aparece.
 *
 * `labelKey` se resuelve con ngx-translate (assets/i18n/es.json → `menu.*`).
 *
 * Con la estructura del catálogo de permisos (`MenuLayout`, de `GET /access/catalog`) el menú
 * sale agrupado, ordenado y nombrado como el catálogo, que el staff edita desde el backoffice
 * (SMARTCLOCK_API/docs/spec-first/specs/access-catalog-layout.spec.md). Sin ella, con los grupos
 * fijos de abajo (`MENU_GROUPS`), que quedan de respaldo.
 */

/**
 * Módulos del menú: el **Módulo DT** reúne todo lo que exige la Res. Ex. N°38/2024 según el
 * formulario de certificación (trabajadores y enrolamiento, marcaciones, modificaciones,
 * documentos y firmas, alertas, reportes, auditoría y la cuenta propia); lo demás va fuera.
 * Es la misma estructura del catálogo de permisos del API (`SECTIONS` / `CATALOG_LAYOUT` en
 * catalog.seed.js): cada pantalla sigue con su permiso propio y aparece sólo si se puede abrir.
 */
export type MenuModuleId = 'inicio' | 'dt' | 'general';

export const MENU_MODULES: Record<MenuModuleId, { labelKey: string; order: number }> = {
  // El Panel de Control, antes que todo (QA ID65; módulo `inicio` del catálogo).
  inicio: { labelKey: 'menu.module.home', order: 0 },
  dt: { labelKey: 'menu.module.dt', order: 1 },
  general: { labelKey: 'menu.module.general', order: 2 },
};

/** Grupos desplegables del sidebar. La clave es la ruta "virtual" del grupo. */
export type MenuGroupId =
  | '/app/management'
  | '/app/dt-clockings'
  | '/app/documents-signatures'
  | '/app/dt'
  | '/app/auditing'
  | '/app/account'
  | '/app/configuration'
  | '/app/billing';

export interface MenuGroupDef {
  labelKey: string;
  /** Material icon name. */
  icon: string;
  /** Módulo al que pertenece (Módulo DT o fuera). */
  module: MenuModuleId;
  /** Orden entre las entradas de primer nivel de su módulo. */
  order: number;
}

/**
 * Grupos del sidebar. El id se conserva igual a la ruta que usaba el menú
 * estático: `sidebar-open-groups` (localStorage) guarda esos ids y el grupo
 * activo se detecta por prefijo de URL.
 */
export const MENU_GROUPS: Record<MenuGroupId, MenuGroupDef> = {
  // ── Módulo DT, en el orden del formulario de certificación ──
  // Trabajadores y enrolamiento (Arts. 7, 12-13, 22.3, 25, 34, 53 y 55).
  '/app/management': { labelKey: 'menu.dtWorkers', icon: 'badge', module: 'dt', order: 10 },
  // Marcaciones (Arts. 7 c, 8-13 y 53): marcar, lo propio y las de la empresa.
  '/app/dt-clockings': { labelKey: 'menu.dtClockings', icon: 'schedule', module: 'dt', order: 20 },
  // 30: Modificaciones de asistencia (entrada de primer nivel, Arts. 39-41).
  // Documentos y firmas (Arts. 43 y 47).
  '/app/documents-signatures': {
    labelKey: 'menu.documentsSignatures',
    icon: 'description',
    module: 'dt',
    order: 40,
  },
  // 50: Alertas (entrada de primer nivel, Art. 45).
  // Reportes DT (Arts. 8 y 25-28): el portal del fiscalizador (Arts. 23-24), los reportes del
  // Art. 27 en su orden, el validador de hash y el listado de empresas (Art. 26).
  '/app/dt': { labelKey: 'menu.dt', icon: 'gavel', module: 'dt', order: 60 },
  // Auditoría (Arts. 14-15 y 22.5). Id virtual: ninguna ruta empieza por él.
  '/app/auditing': { labelKey: 'menu.audit', icon: 'security', module: 'dt', order: 70 },
  // Mi cuenta (Arts. 7 f y 34): contraseña y correos. La tiene cualquier usuario de la empresa.
  '/app/account': { labelKey: 'menu.settings', icon: 'manage_accounts', module: 'dt', order: 80 },
  // ── Módulo de Gestión (lo que la resolución no exige) ──
  '/app/configuration': {
    labelKey: 'menu.configuration',
    icon: 'settings',
    module: 'general',
    order: 30,
  },
  '/app/billing': {
    labelKey: 'menu.billing',
    icon: 'workspace_premium',
    module: 'general',
    order: 40,
  },
};

/**
 * `data.menu` de una ruta. Sin `group` es una entrada de primer nivel (lleva
 * `icon`); con `group` es un subítem de ese grupo desplegable.
 *
 * `order` ordena dentro de su nivel. El orden de primer nivel es global, pero
 * como cada rol ve sólo lo que puede abrir, reproduce el orden que tenía cada
 * menú estático por rol (ver docs/IMPLEMENTATION-LOG.md, 2026-09-26).
 */
export interface RouteMenuData {
  group?: MenuGroupId;
  /**
   * Módulo del catálogo de permisos al que pertenece la pantalla (`resourceId`). Decide en qué
   * sección del menú va y en qué orden, según la estructura del catálogo.
   */
  resource?: string;
  labelKey: string;
  icon?: string;
  order: number;
  /** Sólo en entradas de primer nivel: su módulo (los subítems toman el de su grupo). */
  module?: MenuModuleId;
}

export interface MenuItem {
  /** i18n key under `menu.*`. */
  labelKey: string;
  /** Nombre literal del catálogo; si viene, manda sobre `labelKey`. */
  label?: string;
  /** Absolute router link (for groups: the group id, used for open/active state). */
  route: string;
  /** Material icon name. */
  icon: string;
  /** Collapsible sub-items rendered under this item. */
  children?: MenuChildItem[];
  /** Módulo DT o fuera de él: el layout pone el título del módulo antes de su primera entrada. */
  module: MenuModuleId | string;
  /** Título literal del módulo (del catálogo); si viene, manda sobre `MENU_MODULES`. */
  moduleLabel?: string;
}

export interface MenuChildItem {
  labelKey: string;
  /** Nombre literal del catálogo; si viene, manda sobre `labelKey`. */
  label?: string;
  route: string;
}

/** Módulo del catálogo (Módulo DT, Módulo de Gestión…), en el orden del API. */
export interface MenuLayoutModule {
  id: string;
  label: string;
}

/** Sección del catálogo = grupo del menú; sus módulos del catálogo, en su orden. */
export interface MenuLayoutSection {
  id: string;
  label: string;
  /** Id del módulo (`MenuLayoutModule.id`). */
  module: string;
  /** Ícono de Material Symbols. */
  icon: string | null;
  resources: { resourceId: string; label: string }[];
}

/** Estructura del catálogo de permisos que arma el menú (`GET /access/catalog`). */
export interface MenuLayout {
  modules: MenuLayoutModule[];
  sections: MenuLayoutSection[];
}

/** Predicado de acceso sobre la cadena de `data` raíz → ruta (el del guard). */
export type RouteChainPredicate = (chain: ReadonlyArray<Data | undefined>) => boolean;

interface MenuCandidate {
  route: string;
  menu: RouteMenuData;
}

function joinPath(base: string, path: string | undefined): string {
  if (!path) return base;
  return `${base}/${path}`.replace(/\/+/g, '/');
}

function isRouteMenuData(value: unknown): value is RouteMenuData {
  const menu = value as RouteMenuData | null;
  return !!menu && typeof menu.labelKey === 'string' && typeof menu.order === 'number';
}

function collectCandidates(
  routes: ReadonlyArray<Route>,
  base: string,
  chain: ReadonlyArray<Data | undefined>,
  canOpen: RouteChainPredicate,
  out: MenuCandidate[],
): void {
  for (const route of routes) {
    if (route.redirectTo !== undefined || route.path === '**') continue;
    const path = joinPath(base, route.path);
    const routeChain = [...chain, route.data];
    const menu = route.data?.['menu'];
    if (isRouteMenuData(menu) && !path.includes(':') && canOpen(routeChain)) {
      out.push({ route: path.startsWith('/') ? path : `/${path}`, menu });
    }
    if (route.children?.length) {
      collectCandidates(route.children, path, routeChain, canOpen, out);
    }
  }
}

/**
 * Menú con la estructura del catálogo. Cada pantalla va en la sección donde está su módulo del
 * catálogo (`data.menu.resource`), ordenada por la posición del módulo y luego por su `order`:
 *  - una sección con UNA sola pantalla en todo el router es una entrada de primer nivel con el
 *    nombre de esa pantalla en el catálogo y el ícono de la sección (la sección «Configuración»
 *    con sólo «Notificaciones» decía «Configuración», QA ID68); con dos o más, un grupo
 *    desplegable (id `section:<id>`);
 *  - una pantalla lleva el nombre de su módulo del catálogo si es la única de ese módulo; si el
 *    módulo tiene varias (los cuatro reportes legales), el suyo (`labelKey`);
 *  - el título de cada módulo (Módulo DT, Módulo de Gestión) es el del catálogo.
 * Una pantalla cuyo módulo el catálogo no trae va al final, con su nombre propio.
 */
function buildMenuFromLayout(
  routes: ReadonlyArray<Route>,
  visible: MenuCandidate[],
  layout: MenuLayout,
): MenuItem[] {
  const placeOf = new Map<string, { sectionIndex: number; rank: number; label: string }>();
  layout.sections.forEach((section, sectionIndex) =>
    section.resources.forEach((resource, rank) =>
      placeOf.set(resource.resourceId, { sectionIndex, rank, label: resource.label }),
    ),
  );

  // Cuántas pantallas tiene cada módulo y cada sección en TODO el router (no sólo las visibles):
  // así la forma del menú no cambia según lo que cada rol puede abrir.
  const all: MenuCandidate[] = [];
  collectCandidates(routes, '', [], () => true, all);
  const routesOfResource = new Map<string, number>();
  const routesOfSection = new Map<number, number>();
  for (const { menu } of all) {
    const place = menu.resource ? placeOf.get(menu.resource) : undefined;
    if (!place || !menu.resource) continue;
    routesOfResource.set(menu.resource, (routesOfResource.get(menu.resource) ?? 0) + 1);
    routesOfSection.set(place.sectionIndex, (routesOfSection.get(place.sectionIndex) ?? 0) + 1);
  }

  const moduleIndex = new Map(layout.modules.map((module, index) => [module.id, index]));
  const moduleRank = (id: string) => moduleIndex.get(id) ?? layout.modules.length;
  const moduleLabel = (id: string) => layout.modules.find((module) => module.id === id)?.label;

  const bySection = new Map<
    number,
    { rank: number; order: number; icon?: string; child: MenuChildItem }[]
  >();
  const loose: { order: number; item: MenuItem }[] = [];
  const lastModule = layout.modules[layout.modules.length - 1]?.id ?? 'general';

  for (const { route, menu } of visible) {
    const place = menu.resource ? placeOf.get(menu.resource) : undefined;
    if (!place || !menu.resource) {
      loose.push({
        order: menu.order,
        item: {
          labelKey: menu.labelKey,
          route,
          icon: menu.icon ?? 'chevron_right',
          module: lastModule,
          moduleLabel: moduleLabel(lastModule),
        },
      });
      continue;
    }
    const onlyScreen = routesOfResource.get(menu.resource) === 1;
    const children = bySection.get(place.sectionIndex) ?? [];
    children.push({
      rank: place.rank,
      order: menu.order,
      icon: menu.icon,
      child: { labelKey: menu.labelKey, route, ...(onlyScreen ? { label: place.label } : {}) },
    });
    bySection.set(place.sectionIndex, children);
  }

  const items: { sort: number; item: MenuItem }[] = [];
  for (const [sectionIndex, children] of bySection) {
    const section = layout.sections[sectionIndex];
    children.sort((a, b) => a.rank - b.rank || a.order - b.order);
    const sort = moduleRank(section.module) * 10000 + sectionIndex;
    const owner = { module: section.module, moduleLabel: moduleLabel(section.module) };
    if (routesOfSection.get(sectionIndex) === 1) {
      const [only] = children;
      items.push({
        sort,
        item: {
          ...owner,
          labelKey: only.child.labelKey,
          label: only.child.label ?? section.label,
          route: only.child.route,
          icon: section.icon ?? only.icon ?? 'chevron_right',
        },
      });
    } else {
      items.push({
        sort,
        item: {
          ...owner,
          labelKey: '',
          label: section.label,
          route: `section:${section.id}`,
          icon: section.icon ?? 'folder',
          children: children.map(({ child }) => child),
        },
      });
    }
  }

  return [
    ...items.sort((a, b) => a.sort - b.sort).map(({ item }) => item),
    ...loose.sort((a, b) => a.order - b.order).map(({ item }) => item),
  ];
}

/**
 * Construye el menú lateral desde la configuración del router, con las
 * entradas cuyo predicado de acceso pasa. Los grupos sin hijos visibles no se
 * muestran. Con `layout` (estructura del catálogo) se agrupa y nombra como el
 * catálogo; sin él, con los grupos fijos (`MENU_GROUPS`).
 */
export function buildMenuFromRoutes(
  routes: ReadonlyArray<Route>,
  canOpen: RouteChainPredicate,
  layout: MenuLayout | null = null,
): MenuItem[] {
  const candidates: MenuCandidate[] = [];
  collectCandidates(routes, '', [], canOpen, candidates);
  if (layout?.sections.length) return buildMenuFromLayout(routes, candidates, layout);

  const topLevel: { order: number; item: MenuItem }[] = [];
  const sortKey = (module: MenuModuleId, order: number) =>
    MENU_MODULES[module].order * 1000 + order;
  const groups = new Map<MenuGroupId, { order: number; child: MenuChildItem }[]>();

  for (const { route, menu } of candidates) {
    if (menu.group && MENU_GROUPS[menu.group]) {
      const children = groups.get(menu.group) ?? [];
      children.push({ order: menu.order, child: { labelKey: menu.labelKey, route } });
      groups.set(menu.group, children);
    } else {
      const module = menu.module ?? 'general';
      topLevel.push({
        order: sortKey(module, menu.order),
        item: { labelKey: menu.labelKey, route, icon: menu.icon ?? 'chevron_right', module },
      });
    }
  }

  for (const [groupId, children] of groups) {
    const group = MENU_GROUPS[groupId];
    topLevel.push({
      order: sortKey(group.module, group.order),
      item: {
        labelKey: group.labelKey,
        route: groupId,
        icon: group.icon,
        module: group.module,
        children: children.sort((a, b) => a.order - b.order).map(({ child }) => child),
      },
    });
  }

  return topLevel.sort((a, b) => a.order - b.order).map(({ item }) => item);
}

/** Primera ruta navegable del menú (en orden de aparición). */
export function firstMenuRoute(menu: ReadonlyArray<MenuItem>): string | null {
  for (const item of menu) {
    if (item.children?.length) return item.children[0].route;
    if (!item.children) return item.route;
  }
  return null;
}
